import { WeekDay } from '@angular/common';
import { Component, Input, NgModuleRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import moment from 'moment';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Observable, Subscription } from 'rxjs';

import { BcCalendarEvent, IBcCalendarEventComponent, registerCalendarComponent } from '@bayclubs/calendar';
import { ModalService, SecurityService, TimeService } from '@bayclubs/utils';

import { BcEmployeeAuthFacade } from '@bayclubs/employee-auth';
import { CalendarEventModel, CalendarMode, ContextModel, CopyGroupClassMethod } from '@bayclubs/group-classes-employee-api';
import { HorizonLogger } from '@bayclubs/horizon';
import { gcManagementEnumerations } from '../../../../interfaces/interfaces.module';
import { ManagementContextResolverService } from '../../../../shared/shared.module';
import { SelectInstructorComponent } from '../../select-instructor/select-instructor.component';
import { CalendarEventsService } from '../calendar-events.service';

@Component({
    selector: 'app-calendar-event',
    templateUrl: './calendar-event.component.html',
    styleUrls: ['./calendar-event.component.scss'],
    standalone: false
})
@registerCalendarComponent
export class CalendarEventComponent implements OnInit, OnDestroy, IBcCalendarEventComponent {
    static componentName = 'CalendarEventComponent';

    _event: BcCalendarEvent;
    set event(value: BcCalendarEvent) {
        this._event = value;
        if (value) {
            this.model = value.originalEvent as CalendarEventModel;
            if (this.model.location) {
                this.clubId = this.model.location.clubId;
            } else {
                this.clubId = value.externalType;
            }
            const date = this.timeService.toMoment(value.date).toDate();
            const dayOfWeek = date.getDay();
            if (dayOfWeek === WeekDay.Friday || dayOfWeek === WeekDay.Saturday) {
                this.popoverSide = 'left';
            } else {
                this.popoverSide = 'right';
            }
        }
    }
    get event(): BcCalendarEvent {
        return this._event;
    }
    @Input() public clubId: string;

    @Input() public model: CalendarEventModel;

    @Input() public calendarMode: CalendarMode;

    @Input() public popoverSide: string = 'right';

    @ViewChild('pop', { static: true }) popover: PopoverDirective;

    public context: ContextModel = {
        clubs: [],
        categories: [],
        statuses: [],
        locations: [],
        bookingWindowUnits: [],
        instructors: [],
        classTypes: [],
        streamingServices: [],
        clubGroupClassCategories: [],
        adpPayPeriods: [],
        revenueDepartments: [],
    };

    private subscription: Subscription = new Subscription();

    constructor(
        private router: Router,
        private eventsService: CalendarEventsService,
        private security: SecurityService,
        contextResolverService: ManagementContextResolverService,
        private timeService: TimeService,
        private modalService: ModalService,
        private moduleRef: NgModuleRef<any>,
        private loggingService: HorizonLogger,
        private identityProvider: BcEmployeeAuthFacade
    ) // private classesCalendarFacade: ClassesCalendarFacade
    {
        contextResolverService.getContextObservable().subscribe(context => (this.context = context));
    }

    ngOnInit() {
        if (!this.clubId) {
            throw new Error('"clubId" property cannot be null or empty!');
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    get canCopy(): Observable<boolean> {
        return this.security.hasPrivilege('GroupClasses', 'canManageClasses', this.clubId);
    }

    showPopover($event, pop) {
        this.popover.show();
    }

    closePopover() {
        this.popover.hide();
    }

    public onCopyToTomorrow(): void {
        this.copyClass(CopyGroupClassMethod.CopyToTomorrow);
    }

    public onCopyToEveryDayThisWeek(): void {
        this.copyClass(CopyGroupClassMethod.CopyToEveryDayThisWeek);
    }

    public onCopyToEveryWeekdayThisMonth(): void {
        this.copyClass(CopyGroupClassMethod.CopyToEveryWeekdayThisMonth);
    }

    public onCopyToEveryDayThisMonth(): void {
        this.copyClass(CopyGroupClassMethod.CopyToEveryDayThisMonth);
    }

    public onCopyToNextWeek(): void {
        this.copyClass(CopyGroupClassMethod.CopyToNextWeekday);
    }

    public onEdit(): void {
        this.eventsService.onEditEvent(this.model);

        this.closePopover();
    }

    public onCancel(): void {
        this.eventsService.onCancelEvent(this.model);

        this.closePopover();
    }

    public onRestore(): void {
        this.eventsService.onRestoreEvent(this.model.id, this.model.hasAlreadyStarted);

        this.closePopover();
    }

    public onRemove(): void {
        this.eventsService.onRemoveEvent(this.model);

        this.closePopover();
    }

    public onSubRequest(): void {
        this.eventsService.onSubRequestEvent(this.model);

        this.closePopover();
    }

    public onSubstitute(): void {
        this.eventsService.onSubstituteInstructor(this.model);

        this.closePopover();
    }

    public onCancelSubstitution(): void {
        this.eventsService.onCancelSubstitution(this.model.id);

        this.closePopover();
    }

    public onView(): void {
        this.router.navigate(['management/classes/' + this.model.id]);
    }

    containerClass() {
        if (this.model.location && this.model.location.order > 100) {
            this.loggingService.error(
                'Club with id ' + this.model.location.clubId + ' has more than 100 locations. Define more colors.',
                undefined,
                undefined,
                { userName: this.identityProvider.getLoggedUserNameSnapshot() }
            );
            return 'location-order-other';
        }
        return this.model.location ? `location-order-${this.model.location.order}` : 'location-order-other';
    }

    public isMoreThan15MinutesEvent(): boolean {
        return this.model.slotSpan >= 2;
    }

    public isMoreThan30MinutesEvent(): boolean {
        return this.model.slotSpan >= 3;
    }

    public isMoreThan45MinutesEvent(): boolean {
        return this.model.slotSpan >= 4;
    }

    public get isCancelled(): boolean {
        return this.model.status.code === gcManagementEnumerations.GroupClassStatus.cancelled.code;
    }

    public get canSubstitute(): boolean {
        return (
            !this.isCancelled &&
            this.model.instructor != null &&
            this.model.substituteInstructor == null &&
            this.model.date &&
            !this.classIsFromThePast
        );
    }

    public get canRequestSub(): boolean {
        return (
            !this.isCancelled &&
            !this.classIsFromThePast &&
            (this.model.instructor != null || this.model.substituteInstructor != null)
        );
    }

    public get canCancelSubstitution(): boolean {
        return !this.isCancelled && this.model.substituteInstructor != null;
    }

    public get classIsFromThePast(): boolean {
        const club = this.context.clubs.find(x => x.id === this.clubId);
        if (!club) {
            return false;
        }
        const nowInTimezone = this.timeService.nowInTimeZone(club.timezone);
        const m = this.timeService.toMoment(this.model.date);
        m.add(this.model.timeToInMinutes, 'minutes');
        return m.isBefore(nowInTimezone);
    }

    public get isCopyToEveryDayThisWeekVisible(): boolean {
        const eventDateAsMoment = this.getEventDateAsMoment();

        return !this.timeService.isLastDayOfWeek(eventDateAsMoment) && !this.isRecurring;
    }

    public get isCopyToEveryWeekdayThisMonthVisible(): boolean {
        const eventDateAsMoment = this.getEventDateAsMoment();

        return !this.timeService.isInLastWeekOfMonth(eventDateAsMoment) && !this.isRecurring;
    }

    public get isCopyToEveryDayThisMonthVisible(): boolean {
        const eventDateAsMoment = this.getEventDateAsMoment();

        return !this.timeService.isLastDayOfMonth(eventDateAsMoment) && !this.isRecurring;
    }

    public get isCopyToTomorrowVisible(): boolean {
        return !this.isRecurring;
    }

    public get isCopyToNextWeekVisible(): boolean {
        return !this.isRecurring;
    }

    public get isRecurring(): boolean {
        return this.model.isRecurring;
    }

    private getEventDateAsMoment(): moment.Moment {
        return this.timeService.toMoment(this.model.date);
    }

    private isInstructorActive(): boolean {
        return this.model.instructor && this.model.instructor.isActive;
    }

    private showInstructorSelect(copyGroupClassMethod: CopyGroupClassMethod) {
        const params = { clubId: this.clubId };
        this.subscription.add(
            this.modalService
                .create<SelectInstructorComponent>(SelectInstructorComponent, params, this.moduleRef)
                .subscribe(value => {
                    if (value.copyClass) {
                        const instructorId = value.selectedInstructorModel ? value.selectedInstructorModel.id : null;
                        this.saveCopyClass(copyGroupClassMethod, instructorId);
                    } else {
                        this.showPopover(null, null);
                    }
                })
        );
    }

    private copyClass(copyGroupClassMethod: CopyGroupClassMethod) {
        if (this.isInstructorActive()) {
            this.saveCopyClass(copyGroupClassMethod, null);
        } else {
            this.showInstructorSelect(copyGroupClassMethod);
            this.closePopover();
        }
    }

    private saveCopyClass(copyGroupClassMethod: CopyGroupClassMethod, instructorId: string) {
        this.eventsService.onCopySingle(this.model.id, copyGroupClassMethod, instructorId);
        this.closePopover();
    }
}
